import React from 'react';
import PropTypes from 'prop-types';

const GS_VALUE_REVERSE_PREFIX = '!';

/**
 * Get global registry value by featur name
 *
 * If name starts with `!`, the feature value will be reversed
 *
 * @param {string} name feature name
 * @param {Object} gs globalRegistry Object
 * @return {boolean} featur value
 */
function getGSValue(name, gs) {
  if (typeof name === 'string') {
    if (!gs) {
      return false;
    }

    if (name[0] === GS_VALUE_REVERSE_PREFIX) {
      return !gs.getFeatureValue(name.substr(1));
    }

    return !!gs.getFeatureValue(name);
  }

  return !!name;
}

/**
 * FeatureToggle component
 *
 * @param {Object} param0
 * @param {string} param0.name feature toggle name, use `!` prefix to reverse the feature value
 * @param {string | boolean} param0.and
 * @param {string | boolean} param0.or
 * @param {React.ReactElement | (boolean) => React.ReactChildren} param0.children
 * @param {Object} context
 * @param {Object} context.globalRegistry
 */
function FeatureToggle({name, children, and = true, or = false}, context = {}) {
  const {globalRegistry} = context;
  name = getGSValue(name, globalRegistry);
  or = typeof or === 'boolean' ? or : getGSValue(or, globalRegistry);
  and = typeof and === 'boolean' ? and : getGSValue(and, globalRegistry);

  const on = (name && and) || or;

  if (typeof children === 'function') {
    return children(on);
  }

  return on ? children : null;
}

FeatureToggle.contextTypes = {
  globalRegistry: PropTypes.object,
};

FeatureToggle.propTypes = {
  name: PropTypes.string.isRequired,
  and: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  or: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  children: PropTypes.node,
};

/**
 * Create a HOC applying feature toggle value.
 *
 * If feature toggle is on, will render the wrapped component, otherwise will render null.
 *
 * @param {string} name feature toggle name
 * @return {(component: React.Component) => React.Component}
 */
export function toggle(name) {
  return (Component) =>
    function ToggledComponent(props) {
      return (
        <FeatureToggle name={name}>
          <Component {...props} />
        </FeatureToggle>
      );
    };
}

export default FeatureToggle;
